import React, { useId } from 'react'

// Flat multi-colour service marks.
//
// Every icon is built the same way: one silhouette, clipped into four solid
// brand-colour wedges that meet at the centre. No gradients anywhere. That
// shared construction is what makes the six read as a family even though the
// silhouettes are all different - so if you add a seventh, hand `Mark` a new
// silhouette and it inherits the palette and the cuts for free.
//
// The wedges are oversized triangles (well past the 64x64 box) so they always
// cover whatever silhouette clips them.
const WEDGES = [
  { key: 'n', d: 'M32 32L-20 -20L84 -20Z', fill: '#8c4cf6' }, // @purple
  { key: 'e', d: 'M32 32L84 -20L84 84Z', fill: '#ffc839' }, // @yellow
  { key: 's', d: 'M32 32L84 84L-20 84Z', fill: '#ffa0eb' }, // @pink
  { key: 'w', d: 'M32 32L-20 84L-20 -20Z', fill: '#5119bc' }, // @purple-dark
]

// `children` is the clip silhouette: any SVG shape(s), unioned. useId keeps the
// clipPath id unique per instance, since every one of these renders twice on
// the page (once in a question card, once in its service block) and duplicate
// ids would be invalid markup.
const Mark = ({ children }) => {
  const clipId = `aia-mark-${useId().replace(/:/g, '')}`

  return (
    <svg
      aria-hidden="true"
      focusable="false"
      height="1em"
      role="img"
      viewBox="0 0 64 64"
      width="1em"
    >
      <defs>
        <clipPath id={clipId}>{children}</clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        {WEDGES.map((w) => (
          <path d={w.d} fill={w.fill} key={w.key} />
        ))}
      </g>
    </svg>
  )
}

// start · a spark - the beginning. Points sit at 12/3/6/9 so each one falls
// squarely inside a single wedge and comes out one solid colour.
export const SparkMark = () => (
  <Mark>
    <path d="M32 3C34.6 19.4 44.6 29.4 61 32C44.6 34.6 34.6 44.6 32 61C29.4 44.6 19.4 34.6 3 32C19.4 29.4 29.4 19.4 32 3Z" />
  </Mark>
)

// stall · a group - adoption spreading past the keen few. Dots sit on the
// cardinals, not the diagonals, so none of them straddles a wedge boundary.
export const GroupMark = () => (
  <Mark>
    <circle cx="32" cy="19" r="9.5" />
    <circle cx="45" cy="32" r="9.5" />
    <circle cx="32" cy="45" r="9.5" />
    <circle cx="19" cy="32" r="9.5" />
  </Mark>
)

// deep · productivity up
export const RiseMark = () => (
  <Mark>
    <path d="M32 4L58 30H45.5V58H18.5V30H6L32 4Z" />
  </Mark>
)

// impact · the planet
export const GlobeMark = () => (
  <Mark>
    <circle cx="32" cy="32" r="27" />
  </Mark>
)

// rightsize · footprint and bill coming down - deliberately the mirror of
// RiseMark, since they are the two halves of the same decision
export const ReduceMark = () => (
  <Mark>
    <path d="M32 60L6 34H18.5V6H45.5V34H58L32 60Z" />
  </Mark>
)

// rules · governance
export const ShieldMark = () => (
  <Mark>
    <path d="M32 4L56 12.5V32C56 46 45.6 56.6 32 60C18.4 56.6 8 46 8 32V12.5L32 4Z" />
  </Mark>
)
